import React from 'react'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import { GeneratedForms } from '../../../types/Templates'
import firebase from 'firebase'
import DateTimeView from '../../atoms/DateTimeView'
import Spacer from '../../atoms/Spacer'
import {
  FormsTableCol,
  FormsTableRow,
} from '../../../pages/CompositeCoursePage/styled'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'
import theme from '@app/ui/theme'

type CompositeFinalRatingItemProps = {
  form: GeneratedForms
  showArchiveFormsModal: (formId: string) => void
  onUpdateTitle?: (formId: string, title: string) => Promise<void>
  variant?: 'card' | 'row'
}

export const downloadFolder = (ref: string) => {
  const doc = firebase.storage().ref(ref)
  doc.getDownloadURL().then((url) => {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${document.title || 'Composite_Course_Forms'}.zip`
    console.log({ url, title: document.title })
    anchor.click()
  })
}

const KebabMenu: React.FC<{
  form: GeneratedForms
  showArchiveFormsModal: (formId: string) => void
  onUpdateTitle?: (formId: string, title: string) => Promise<void>
}> = ({ form, showArchiveFormsModal, onUpdateTitle }) => {
  const [open, setOpen] = React.useState(false)
  const [editing, setEditing] = React.useState(false)
  const [titleValue, setTitleValue] = React.useState(form.title ?? '')
  const menuRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
        setEditing(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleSave = async () => {
    await onUpdateTitle?.(form.id, titleValue)
    setEditing(false)
    setOpen(false)
  }

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'none',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          padding: '4px 8px',
          display: 'flex',
          alignItems: 'center',
          height: 40,
          boxSizing: 'border-box',
        }}
      >
        <FeatherIcon name="more-vertical" size={16} color="#333" />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '110%',
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            zIndex: 100,
            minWidth: 200,
            padding: '6px 0',
          }}
        >
          {editing ? (
            <div style={{ padding: '6px 8px', display: 'flex', gap: 6 }}>
              <input
                autoFocus
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave()
                  if (e.key === 'Escape') {
                    setEditing(false)
                    setTitleValue(form.title ?? '')
                  }
                }}
                style={{
                  flex: 1,
                  border: '1px solid #ccc',
                  borderRadius: 5,
                  padding: '0 12px',
                  fontSize: 14,
                  height: 40,
                  boxSizing: 'border-box',
                }}
              />
              <button
                onClick={handleSave}
                style={{
                  background: '#151A20',
                  border: 'none',
                  borderRadius: 5,
                  cursor: 'pointer',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FeatherIcon name="check" size={16} color="#fff" />
              </button>
              <button
                onClick={() => {
                  setEditing(false)
                  setTitleValue(form.title ?? '')
                }}
                style={{
                  background: 'none',
                  border: '1px solid #ccc',
                  borderRadius: 5,
                  cursor: 'pointer',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FeatherIcon name="x" size={16} color="#333" />
              </button>
            </div>
          ) : (
            <MenuItem
              icon="edit-2"
              label="Edit title"
              onClick={() => setEditing(true)}
            />
          )}
          <div style={{ borderTop: '1px solid #eee', margin: '4px 0' }} />
          <MenuItem
            icon="trash-2"
            label="Delete"
            color="#d35"
            onClick={() => {
              showArchiveFormsModal(form.id)
              setOpen(false)
            }}
          />
        </div>
      )}
    </div>
  )
}

const MenuItem: React.FC<{
  icon: string
  label: string
  color?: string
  onClick: () => void
}> = ({ icon, label, color = '#333', onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      width: '100%',
      height: 40,
      padding: '0 18px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: 14,
      color,
      textAlign: 'left',
      boxSizing: 'border-box',
    }}
    onMouseEnter={(e) =>
      ((e.currentTarget as HTMLButtonElement).style.background = '#f5f5f5')
    }
    onMouseLeave={(e) =>
      ((e.currentTarget as HTMLButtonElement).style.background = 'none')
    }
  >
    <FeatherIcon name={icon} size={16} color={color} />
    {label}
  </button>
)

const CompositeFinalRatingItem: React.FC<CompositeFinalRatingItemProps> = ({
  form,
  showArchiveFormsModal,
  onUpdateTitle,
  variant = 'card',
}) => {
  if (variant === 'row') {
    return (
      <FormsTableRow>
        <FormsTableCol>{form.title ?? '-'}</FormsTableCol>
        <FormsTableCol>{form.name ?? '-'}</FormsTableCol>
        <FormsTableCol>{form.courseName ?? '-'}</FormsTableCol>
        <FormsTableCol>
          <DateTimeView date={form.ratingDate} showTime={false} />
        </FormsTableCol>
        <FormsTableCol>
          <DateTimeView
            date={form.createdAt ? new Date(form.createdAt) : new Date()}
          />
        </FormsTableCol>
        <FormsTableCol style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            <KebabMenu
              form={form}
              showArchiveFormsModal={showArchiveFormsModal}
              onUpdateTitle={onUpdateTitle}
            />
            <RoundedButton
              title="DOWNLOAD FORMS"
              onPress={() => downloadFolder(form.zipPath)}
              style={{ backgroundColor: theme.colors.primary2 }}
            />
          </div>
        </FormsTableCol>
      </FormsTableRow>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <h4 style={{ marginBlockEnd: '0.5em' }}>Composite Forms Details</h4>
        <KebabMenu
          form={form}
          showArchiveFormsModal={showArchiveFormsModal}
          onUpdateTitle={onUpdateTitle}
        />
      </div>
      <p style={{ marginBlock: '0.5em' }}>
        Generated:{' '}
        <DateTimeView
          date={form.createdAt ? new Date(form.createdAt) : new Date()}
        />
      </p>
      <p>Title: {form.title}</p>
      <Spacer height={4} />

      <p>Rating Name: {form?.name}</p>
      <p>
        Rating Date:{' '}
        <DateTimeView date={form.ratingDate} showTime={false} />
      </p>
      <p>Club Name: {form?.clubName}</p>
      <p>Course Name: {form?.courseName}</p>
      <div style={{ marginBlockEnd: '2em' }}>
        <RoundedButton
          title="Download Forms"
          onPress={() => downloadFolder(form.zipPath)}
        />
      </div>
    </div>
  )
}
export default CompositeFinalRatingItem
